/* src/main.js (Vue 3) */
import { createApp } from "vue";
import App from "./App.vue";
import { createRouter, createWebHistory } from "vue-router";
import routes from "./services/routes.js";
import store from "./services/vuex.js";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import VueSweetalert2 from "vue-sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import VueLazyLoad from "vue3-lazyload";
import http from "./services/http";
import api from "./services/apiEndpoints";
import auth from "./services/auth.js"; // singleton instance
import eventBus from "./services/eventBus.js";
import moment from "moment";
import FloatingVue from "floating-vue";
import "floating-vue/dist/style.css";

import { createBootstrap } from "bootstrap-vue-next";
// Import Bootstrap and BootstrapVueNext CSS
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue-next/dist/bootstrap-vue-next.css";

import VueGoogleMaps from "@fawmi/vue-google-maps";
import VueSignaturePad from "vue3-signature-pad";
import generalOptions from "./services/config.js";
import message from "./services/messages.js";
// Import FA4 CSS
import "font-awesome/css/font-awesome.css";

// Vue I18n
import { createI18n } from "vue-i18n";
import en from "./languages/en.js";
import es from "./languages/es.js";

import { vMaska } from "maska/vue";

const messages = { en, es };
const i18n = createI18n({
    locale: "en",
    fallbackLocale: "en",
    messages,
});

/* --- Filters --- */
const filters = {
    phoneDashFormat(phone) {
        if (!phone) return "";
        phone = phone.toString();
        if (phone.length < 10) return phone;
        return `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
    },
    capitalize(value) {
        return value ? value.toString().charAt(0).toUpperCase() + value.slice(1) : "";
    },
    dateFormat(date) {
        return date ? moment(date).format("D MMM") : "";
    },
    monthDateFormat(date) {
        return date ? moment(date).format("MMMM Do") : "";
    },
    dateFullFormat(date) {
        return date ? moment(date).format("DD/MM/YYYY") : "";
    },
};

/* --- Router setup --- */
const router = createRouter({
    history: createWebHistory(),
    routes,
});

/* --- Router Guards --- */
router.beforeEach((to, from, next) => {
    try {
        const token = auth.state.token;
        const user = auth.state.user;

        // Public/private route checks
        if (auth.private_routes?.includes(to.path) && !token) return next("/");
        if (auth.private_admin_routes?.includes(to.path) && !token) {
            eventBus.emit("user_logged_out");
            return next("/app/login");
        }

        // Subscription check for stylists
        if (
            auth.private_admin_routes?.includes(to.path) &&
            user?.role === "stylist" &&
            user.details?.subscription &&
            !user.details.subscription.active &&
            !["/app/subscription-payments", "/app/profile"].includes(to.path)
        ) {
            return next("/app/subscription-payments");
        }

        if (to.path.includes("/app")) eventBus.emit("admin_route_loaded");

        // Token refresh handling
        if (token && Date.now() / 1000 >= token.expire_time) {
            if (typeof auth.refreshToken === "function") {
                auth.refreshToken(() => next());
            } else {
                console.warn("refreshToken not available, redirecting to login");
                next("/app/login");
            }
        } else {
            next();
        }
    } catch (e) {
        console.log("error in router.beforeEach: ", e);
    }
});

/* --- Create Vue App --- */
const app = createApp(App);

/* --- Global properties --- */
app.config.globalProperties.$auth = auth;
app.config.globalProperties.$http = http;
app.config.globalProperties.$eventBus = eventBus;
app.config.globalProperties.$filters = filters;
app.config.globalProperties.$api = api;
app.config.globalProperties.$ml = (key, values = {}) => i18n.global.t(key, values);

// Global script loader
app.config.globalProperties.$loadScript = function (src) {
    return new Promise((resolve, reject) => {
        // Prevent duplicate loading
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

/* --- Plugins --- */
app.use(router);
app.use(store);
app.use(Toast);
app.use(VueSweetalert2);
app.use(VueLazyLoad);
app.use(FloatingVue);
app.use(createBootstrap);
app.use(VueGoogleMaps, {
    load: {
        key: import.meta.env.VITE_MAPS_API_KEY,
        libraries: "places",
    },
    installComponents: true,
});
app.use(generalOptions);
app.use(VueSignaturePad);
app.use(i18n);

app.directive("maska", vMaska);

/* --- Mount App --- */
app.mount("#app");
